//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
import * as Sentry from '@sentry/react';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { get, has } from 'lodash';
import queryString from 'query-string';
import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useActionPathLog, useActiveClassBrodcastMessage, useActiveClassUrl, useStudentClassPathLogListner } from 'store/live_logs/selectors';
import { useTimmerRunning } from 'store/timmer/selectors';
import { useCurrentUser } from 'store/users/selectors';
import { Hub } from 'aws-amplify/utils';
import { prefetchAllWords } from 'utils/methods/word_filter';

Hub.listen('auth', ({ payload }) => {
  switch (payload.event) {
    case 'signedIn':
      break;
    case 'signedOut':
      break;
    case 'tokenRefresh':
      break;
    case 'tokenRefresh_failure':
      break;
    case 'signInWithRedirect':
      break;
    case 'signInWithRedirect_failure':
      break;
    case 'customOAuthState':
      console.info('custom state returned from CognitoHosted UI');
      break;
  }
});
const GlobalEffects = ({ children }) => {
  const showAlert = useAlert();
  const currentUser = useCurrentUser();
  const { updateFirebaseOnClose } = useActionPathLog();
  useStudentClassPathLogListner();
  useActiveClassBrodcastMessage();
  const activeUrl = useActiveClassUrl();
  const [timmerRunning, ,] = useTimmerRunning();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() =>{
    if(location.search){
      return queryString.parse(location.search);
    }
    return {};
  }, [location.search]);


  useEffect(() => {
    const handleContextMenu = (event) => {
      if (window.location.origin.includes("localhost")) {
        return;
      }
      event.preventDefault();
    };
    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      updateFirebaseOnClose();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (activeUrl && currentUser?.role === 'student') {
      try {
        if (has(searchParams, "activity") || has(searchParams, "quiz")) {
          if (searchParams.activity == 'null' && searchParams.quiz == 'null') {
            navigate(activeUrl);
          } else {
            const finish_later = document.getElementById('finish_later');
            if (finish_later) {
              finish_later.click();
            }
            setTimeout(() => {
              navigate(activeUrl);
            }, 1000);
          }
        } else {
          navigate(activeUrl);
        }
      } catch (error) {
        console.error('Error handling active URL navigation:', error);
        Sentry.captureException(`Error in GlobalEffects navigation: ${error}`);
      }
    }
  }, [activeUrl, searchParams, currentUser?.role, navigate]);

  useEffect(() => {
    const preventPrint = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'p') {
        event.preventDefault();
        showAlert('Printing is disabled on this page!', 'error');
      }

      if (event.key === 'PrintScreen') {
        event.preventDefault();
        showAlert('Screenshots are disabled on this page!', 'error');
      }
    };

    // Add event listener for keydown
    document.addEventListener('keydown', preventPrint);

    // Cleanup event listener on component unmount
    return () => {
      document.removeEventListener('keydown', preventPrint);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (timmerRunning) {
        const message = "Timer-based assignments can't be 'finished later', it will be submitted automatically for you if you click on it.";
        event.returnValue = message;
        return message;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [timmerRunning]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      try {
        const disableForPage  = ["/login"];
        if(disableForPage.includes(location.pathname)|| window.location.origin.includes("localhost")) return;
        if (currentUser) {
          const target = event.target;
          const isGameCodeInput = target.placeholder === "Enter code";
          
          if ((event.ctrlKey || event.metaKey) && (event.key === 'v' || event.key === "V") && get(currentUser, "role") === "student" && !isGameCodeInput) {
            event.preventDefault();
            showAlert('Pasting is not allowed!', 'error');
          } else if ((event.ctrlKey || event.metaKey) && (event.key === 'c' || event.key === "C") && get(currentUser, "role") === "student" && !isGameCodeInput) {
            event.preventDefault();
            showAlert('Copying is not allowed!', 'error');
          } else if ((event.ctrlKey || event.metaKey) && (event.key === 'x' || event.key === "X") && get(currentUser, "role") === "student" && !isGameCodeInput) {
            event.preventDefault();
            showAlert('Cutting is not allowed!', 'error');
          }
        }
      } catch (error) {
        Sentry.captureException(`411 Error during handleKeyDown: ${error}`);
        console.log(error);
      }
    };

    const handlePaste = (e) => {
      try {
        const disableForPage  = ["/login"];
        if(disableForPage.includes(location.pathname)) return;
        if (currentUser) {
          const target = e.target;
          const isGameCodeInput = target.placeholder === "Enter code";
          if (get(currentUser, "role") === "student" && !isGameCodeInput) {
            e.preventDefault();
            showAlert('Pasting is not allowed!', 'error');
          }
        }
      } catch (error) {
        Sentry.captureException(`412 Error during handlePaste: ${error}`);
        console.log(error);
      }
    };

    // Attach event listeners
    if(currentUser && currentUser?.role === "student"){
      document.addEventListener('paste', handlePaste);
      document.addEventListener('keydown', handleKeyDown);
    }


    // Cleanup event listeners on component unmount
    return () => {
      if(currentUser && currentUser?.role === "student"){
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('paste', handlePaste);
      }
    };
  }, [currentUser,location.pathname]);

  useEffect(() => {
    if (currentUser?.id) {
      prefetchAllWords().catch(error => {
        console.error('Error prefetching words:', error);
        Sentry.captureException(`Error prefetching inappropriate words: ${error}`);
      });
    }
  }, [currentUser?.id]);

  return <>{children}</>;
};

export default GlobalEffects; 